/**
 * reframe.js — turning a finished 16:9 clone composition into a 9:16 one for
 * Reels and Shorts, without destroying the thing that makes it a clone
 * composition: both of you, in one frame.
 *
 * Every rule below was picked by measurement rather than taste. Four ways of
 * getting a two-performer 16:9 frame into 9:16 were scored on the rendered
 * output — how much of each performer survives, how often BOTH are visible,
 * and how much of the result is real picture:
 *
 *   centre crop   kept 0.00 / 0.00, both visible 0%   ← what most tools do
 *   pan to speaker kept 0.66 / 0.30, both visible 0%
 *   blurred fit   kept 1.00 / 1.00, 32% real picture
 *   STACK         kept 1.00 / 1.00, both visible 90%, 100% real picture
 *
 * So: stack. But stacking naively — each cell filled from its own side of the
 * seam — has a defect that only shows up in numbers. The seam is rarely at the
 * middle, so the two regions are different widths, and filling equal cells from
 * unequal regions scales the halves differently. Measured at a seam of 0.46 the
 * two clones came out 17% different in size; at a seam of 0.25, 153% different.
 * On a tool whose entire promise is that both people are the same person, that
 * is not a rounding error.
 *
 * The fix is to derive ONE window size for both cells. The obvious source for
 * it is the narrower region — and that is also wrong: measured on takes with
 * the performers near the frame edges, a window sized and centred on the region
 * lost the second performer ENTIRELY (kept 0.00), because the region is an
 * accident of where the seam sits while the performer is the actual subject.
 *
 * What survives every case is: size the window from the LARGER PERFORMER, use
 * that one size for both cells, and centre each on its own performer, clamped
 * to the frame rather than to the region. Measured on both a centred pair and
 * an edge-standing pair with an off-centre seam:
 *
 *              kept A/B   whole   scale ratio   picture   other clone
 *   naive      0.66/1.00   0%        2.531       100%        0.0%
 *   by region  0.66/0.00   0%        1.000       100%        0.0%
 *   by subject 0.71/0.71   0%        1.000       100%        0.0%
 *   fit        1.00/1.00 100%        1.185        62%        0.0%
 *   THIS       1.00/1.00 100%        1.000       100%        0.0%
 *
 * Exposes window.CloneReframe.
 */
(function () {
'use strict';
if (window.CloneReframe) return;

var DEG = Math.PI / 180;
var MARGIN = 1.18;          // breathing room around a body, measured as enough
var SAMPLES = 5;            // frames sampled to union a performer's movement

/**
 * Which side of the seam a point is on, in the same convention masks.js uses:
 * `pos` runs across the whole projection of the frame onto the seam normal, so
 * the test holds at any angle and not only for a vertical split.
 */
function sideOf(angleDeg, pos, cw, ch, px, py) {
    var a = angleDeg * DEG;
    var nx = Math.cos(a), ny = Math.sin(a);
    var R = (cw * Math.abs(nx) + ch * Math.abs(ny)) / 2;
    var offset = (pos * 2 - 1) * R;
    var cx = cw / 2 + nx * offset, cy = ch / 2 + ny * offset;
    return ((px - cx) * nx + (py - cy) * ny) < 0 ? 0 : 1;
}

/**
 * The two performers' boxes, read out of a difference field.
 *
 * The takes share a background, so wherever they DISAGREE is where somebody is
 * standing — one blob per performer, and the seam says which is which. This is
 * the same field the seam analysis already computes, so no new machinery and no
 * model is involved.
 *
 * Percentile trimming rather than a plain bounding box: a single lit speckle
 * from sensor noise would otherwise stretch a box across the frame, and the
 * window sized from it would zoom all the way out.
 *
 * @returns [boxA, boxB] in NORMALISED 0..1 coordinates, or nulls
 */
function boxesFromField(field, fw, fh, angleDeg, pos, threshold) {
    var thr = threshold == null ? 14 : threshold;
    var xs = [[], []], ys = [[], []];

    for (var y = 0; y < fh; y++) {
        for (var x = 0; x < fw; x++) {
            if (field[y * fw + x] < thr) continue;
            var s = sideOf(angleDeg, pos, fw, fh, x + 0.5, y + 0.5);
            xs[s].push(x); ys[s].push(y);
        }
    }

    return [0, 1].map(function (s) {
        if (xs[s].length < 12) return null;              // nothing convincing there
        xs[s].sort(function (a, b) { return a - b; });
        ys[s].sort(function (a, b) { return a - b; });
        var q = function (arr, p) { return arr[Math.max(0, Math.min(arr.length - 1, Math.round(p * (arr.length - 1))))]; };
        var x0 = q(xs[s], 0.02), x1 = q(xs[s], 0.98);
        var y0 = q(ys[s], 0.02), y1 = q(ys[s], 0.98);
        return {
            x: x0 / fw, y: y0 / fh,
            w: Math.max(1, x1 - x0 + 1) / fw,
            h: Math.max(1, y1 - y0 + 1) / fh
        };
    });
}

/**
 * Where EACH performer stands, for any number of takes.
 *
 * With two takes the difference between them lights up both performers and the
 * seam says which is which. With three or four that trick has nothing to split
 * on — so the answer comes from an intersection instead.
 *
 * Take i differs from take j wherever EITHER performer stands. Performer j
 * lights up in diff(i,j) but nowhere else, because in diff(i,k) both takes show
 * plain background at j's spot. So the region that lights up in EVERY diff
 * involving i is performer i, and nobody else's.
 *
 * @param fields  { "i_j": Float32Array }  pairwise difference fields, i < j
 * @returns [box|null, …] per take, in NORMALISED 0..1 coordinates
 */
function performerBoxes(fields, n, fw, fh, threshold) {
    var thr = threshold == null ? 14 : threshold;
    var out = [];
    for (var i = 0; i < n; i++) {
        var xs = [], ys = [];
        for (var y = 0; y < fh; y++) {
            for (var x = 0; x < fw; x++) {
                var p = y * fw + x, all = true;
                for (var j = 0; j < n && all; j++) {
                    if (j === i) continue;
                    var f = fields[Math.min(i, j) + '_' + Math.max(i, j)];
                    if (!f || f[p] < thr) all = false;
                }
                if (all) { xs.push(x); ys.push(y); }
            }
        }
        if (xs.length < 12) { out.push(null); continue; }
        xs.sort(function (a, b) { return a - b; });
        ys.sort(function (a, b) { return a - b; });
        var q = function (arr, pr) { return arr[Math.max(0, Math.min(arr.length - 1, Math.round(pr * (arr.length - 1))))]; };
        var x0 = q(xs, 0.02), x1 = q(xs, 0.98), y0 = q(ys, 0.02), y1 = q(ys, 0.98);
        out.push({ x: x0 / fw, y: y0 / fh, w: Math.max(1, x1 - x0 + 1) / fw, h: Math.max(1, y1 - y0 + 1) / fh });
    }
    return out;
}

/**
 * Vertical bands, one per take, with the edges placed BETWEEN the performers
 * rather than at even thirds — an even split cuts whoever is not standing where
 * the arithmetic expected.
 *
 * @returns [{ index, x0, x1 }] sorted left to right, normalised
 */
function bandsFor(boxes) {
    var known = [];
    for (var i = 0; i < boxes.length; i++) {
        if (boxes[i]) known.push({ index: i, c: boxes[i].x + boxes[i].w / 2, box: boxes[i] });
    }
    if (known.length < 2) return null;
    known.sort(function (a, b) { return a.c - b.c; });

    var out = [];
    for (var k = 0; k < known.length; k++) {
        var lo = k === 0 ? 0 : (known[k - 1].c + known[k].c) / 2;
        var hi = k === known.length - 1 ? 1 : (known[k].c + known[k + 1].c) / 2;
        // Never let a band cut into the body it is supposed to contain.
        lo = Math.min(lo, known[k].box.x - 0.005);
        hi = Math.max(hi, known[k].box.x + known[k].box.w + 0.005);
        out.push({ index: known[k].index, x0: Math.max(0, lo), x1: Math.min(1, hi) });
    }
    return out;
}

/** Union of the same performer's box across sampled times — they move. */
function unionBox(list) {
    var out = null;
    for (var i = 0; i < list.length; i++) {
        var b = list[i];
        if (!b) continue;
        if (!out) { out = { x: b.x, y: b.y, w: b.w, h: b.h }; continue; }
        var x1 = Math.max(out.x + out.w, b.x + b.w), y1 = Math.max(out.y + out.h, b.y + b.h);
        out.x = Math.min(out.x, b.x); out.y = Math.min(out.y, b.y);
        out.w = x1 - out.x; out.h = y1 - out.y;
    }
    return out;
}

/**
 * The two source windows, in COMPOSITION pixels.
 *
 * One size for both — that is the whole point — derived from whichever
 * performer needs more room, then clamped to the frame. Each window is centred
 * on its own performer; a performer whose box is unknown falls back to the
 * centre of their side of the seam, which is the honest guess rather than a
 * confident wrong one.
 */
function windowsFor(boxes, cw, ch, cellAspect, angleDeg, pos) {
    var n = boxes.length, need = 0, i;
    for (i = 0; i < n; i++) {
        var b = boxes[i];
        if (!b) continue;
        need = Math.max(need, b.w * cw * MARGIN, b.h * ch * MARGIN * cellAspect);
    }
    if (!(need > 0)) need = cw / 2;

    var w = Math.min(need, cw);
    var h = w / cellAspect;
    if (h > ch) { h = ch; w = Math.min(cw, ch * cellAspect); }

    // Where each side's centre falls, for the performer we could not find.
    var a = angleDeg * DEG, nx = Math.cos(a), ny = Math.sin(a);
    var R = (cw * Math.abs(nx) + ch * Math.abs(ny)) / 2;
    var off = (pos * 2 - 1) * R;
    var sx = cw / 2 + nx * off, sy = ch / 2 + ny * off;

    var out = [];
    for (i = 0; i < n; i++) {
        var bb = boxes[i];
        var cx, cy;
        if (bb) { cx = (bb.x + bb.w / 2) * cw; cy = (bb.y + bb.h / 2) * ch; }
        else if (n === 2) {
            // Two takes: midway between the seam and the far corner of that side.
            var dir = i === 0 ? -1 : 1;
            cx = (sx + (dir < 0 ? 0 : cw)) / 2;
            cy = Math.abs(ny) > Math.abs(nx) ? (sy + (dir < 0 ? 0 : ch)) / 2 : ch / 2;
        } else {
            // More takes: an even share of the frame is the honest guess when
            // the performer could not be located.
            cx = cw * (i + 0.5) / n;
            cy = ch / 2;
        }
        out.push({
            x: Math.max(0, Math.min(cw - w, cx - w / 2)),
            y: Math.max(0, Math.min(ch - h, cy - h / 2)),
            w: w, h: h
        });
    }

    // Reading order: whoever is further left (or higher, for a horizontal seam)
    // goes in the top cell, so the stack matches how the composition reads.
    var horizontal = Math.abs(Math.sin(angleDeg * DEG)) > Math.abs(Math.cos(angleDeg * DEG));
    out.sort(function (p, q) { return horizontal ? (p.y - q.y) : (p.x - q.x); });
    return out;
}

/**
 * How many cells, and in what arrangement.
 *
 * Two takes stack. Three stack too — a 2x2 with one empty cell wastes a quarter
 * of a format whose whole point is that vertical space is precious. Four go in
 * a 2x2, because four rows in 9:16 leave each person a 9:4 letterbox slot that
 * no standing body fits.
 *
 * @returns { cols, rows, cells: [{x,y,w,h}] } in 0..1 of the output
 */
function gridFor(n, ow, oh) {
    var cols = n >= 3 ? 2 : 1;
    var rows = Math.ceil(Math.max(1, n) / cols);
    var cells = [];
    for (var i = 0; i < n; i++) {
        var cx = i % cols, cy = Math.floor(i / cols);
        cells.push({ x: cx / cols, y: cy / rows, w: 1 / cols, h: 1 / rows });
    }
    return { cols: cols, rows: rows, cells: cells, aspect: (ow / cols) / (oh / rows) };
}

/** The output canvas size for a project's framing. */
function outputSize(project) {
    var f = project && project.frame;
    if (!f || f.mode !== 'vertical') return { w: project.W, h: project.H };
    // Keep the long side at the composition's height so nothing is upscaled
    // beyond what the source can carry, rounded to even for the encoders.
    var h = Math.max(2, Math.round(project.H / 2) * 2);
    var w = Math.max(2, Math.round(h * 9 / 16 / 2) * 2);
    return { w: w, h: h };
}

/** Aspect of a single cell in the stack, for a given number of takes. */
function cellAspect(out, n) { return gridFor(n == null ? 2 : n, out.w, out.h).aspect; }

/**
 * Draw a rendered composition into the vertical output.
 *
 * The cells are filled, not letterboxed: the windows were sized to the cell
 * aspect, so the only time bars appear is when the composition itself is
 * narrower than a cell — and then they appear equally in both, which keeps the
 * two clones the same size.
 */
function drawVertical(ctx, base, win, ow, oh, bg) {
    var grid = gridFor(win.length, ow, oh);
    ctx.save();
    ctx.fillStyle = bg || '#000';
    ctx.fillRect(0, 0, ow, oh);
    for (var i = 0; i < win.length; i++) {
        var w = win[i], cell = grid.cells[i];
        if (!w || !cell) continue;
        var cxp = cell.x * ow, cyp = cell.y * oh, cwp = cell.w * ow, chp = cell.h * oh;
        var s = Math.min(cwp / w.w, chp / w.h);
        var dw = w.w * s, dh = w.h * s;
        ctx.drawImage(base, w.x, w.y, w.w, w.h,
            cxp + (cwp - dw) / 2, cyp + (chp - dh) / 2, dw, dh);
    }
    ctx.restore();
}

window.CloneReframe = {
    SAMPLES: SAMPLES,
    performerBoxes: performerBoxes,
    gridFor: gridFor,
    bandsFor: bandsFor,
    boxesFromField: boxesFromField,
    unionBox: unionBox,
    windowsFor: windowsFor,
    outputSize: outputSize,
    cellAspect: cellAspect,
    drawVertical: drawVertical
};
})();
